import { MONTHS } from '@/common/constants';
import { ActionInspectDialog } from '@/components/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AgrotechContour } from '@/features/agro-technical-events/components/table/AgrotechContour';
import { useTable } from '@/features/agro-technical-events/hooks';
import useAgroTechnicalEventsStore from '@/features/agro-technical-events/store/useAgroTechnicalEventsStore';
import { useMonitoringStore } from '@/features/monitoring/store/useMonitoringStore';
import { Infos } from '@/features/projects/components/single/info';
import useProjectStore from '@/features/projects/store/useProjectStore';
import { IContourAction } from '@/features/stewardship/models/IStewardship';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

export const Agrotechnical: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { id, district_id } = useParams();
  const plant_id = searchParams.get('plant_id');
  const month = searchParams.get('month') || 'JANUARY';
  const { t } = useTranslation();
  const { columns } = useTable();

  // Project info
  const { projectsById, projectsByIdLoading, fetchProjectById } =
    useProjectStore();

  // Area data — monitoring store
  const { areaData, fetchFarmersArea } = useMonitoringStore();

  // Contours & events — agrotech store
  const {
    agroTechnicalEventProjectContours,
    agroTechnicalEventProjectContoursLoading,
    agroEvents,
    agroEventsLoading,
    fetchAgroTechnicalEventProjectContours,
    fetchAgroEvents,
    actionInspectData,
    actionInspectLoading,
    fetchActionInspect,
    clearActionInspectData,
  } = useAgroTechnicalEventsStore();

  // Local state
  const [activeContour, setActiveContour] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<IContourAction | null>(
    null,
  );

  // Fetch project
  useEffect(() => {
    if (id) {
      fetchProjectById({ id });
    }
  }, [id]);

  // Fetch area
  useEffect(() => {
    fetchFarmersArea({
      district_id,
      project_id: id,
      plant_id: plant_id || '2',
    });
  }, [id, district_id, plant_id]);

  // Set default month
  useEffect(() => {
    if (!searchParams.get('month')) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          const currentMonthValue = MONTHS?.find(
            (_, i) => i === new Date().getMonth(),
          )?.value;
          newParams.set('month', String(currentMonthValue));
          return newParams;
        },
        { replace: true },
      );
    }
  }, []);

  const handleChangeTab = (value: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('month', value);
        return newParams;
      },
      { replace: true },
    );
    setActiveContour('');
  };

  // Fetch contours when month changes
  useEffect(() => {
    if (id && month) {
      fetchAgroTechnicalEventProjectContours({ id, month });
      setActiveContour('');
    }
  }, [id, month]);

  // Fetch events when contour is expanded
  useEffect(() => {
    if (activeContour && id && month) {
      const crop_id = agroTechnicalEventProjectContours.find(
        (c) => c.contour_id === activeContour,
      )?.crop_id;
      fetchAgroEvents({
        month,
        contour_id: activeContour,
        project_id: id,
        crop_id,
      });
    }
  }, [activeContour, id, month]);

  // Dialog
  const handleOpen = (action: IContourAction) => {
    setSelectedAction(action);
    setOpenModal(true);
  };

  useEffect(() => {
    if (openModal && selectedAction) {
      fetchActionInspect({
        farmer_action_by_project_id: selectedAction.farmer_action_by_project_id,
        farmer_action_by_project_contour_id:
          selectedAction.farmer_action_by_project_contour_id,
        contour_id: activeContour,
      });
    } else if (!openModal) {
      setSelectedAction(null);
    }
  }, [openModal, selectedAction]);

  return (
    <div className="flex flex-col gap-8 p-8  overflow-y-auto">
      <Infos items={projectsById} isLoading={projectsByIdLoading} />

      <hr />
      <div className="flex flex-col gap-4">
        <p className="text-lg font-medium">
          {t('total.land.area')}:{' '}
          <span className="text-orange-default">
            {Number(areaData.area || 0)?.toFixed(2)} {t('ga')}
          </span>
        </p>
        <Tabs value={month} onValueChange={handleChangeTab} className="w-full">
          <TabsList className="grid w-[1000px] grid-cols-12">
            {MONTHS.map((item) => (
              <TabsTrigger value={item.value} key={item.value}>
                {t(item.value)}
              </TabsTrigger>
            ))}
          </TabsList>
          {MONTHS.map((item) => (
            <TabsContent value={item.value} key={item.value}>
              <div className="mt-4 flex flex-col gap-5">
                <h4 className="text-lg font-semibold">{t('all.contours')}</h4>
                {agroTechnicalEventProjectContoursLoading ? (
                  <Skeleton className="h-[200px] w-full rounded-xl" />
                ) : (
                  <AgrotechContour
                    contours={agroTechnicalEventProjectContours}
                    activeContour={activeContour}
                    onContourChange={setActiveContour}
                    agroEvents={agroEvents}
                    eventsLoading={agroEventsLoading}
                    columns={columns}
                    pageSize={10}
                    currentPage={1}
                    onRowClick={handleOpen}
                  />
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <ActionInspectDialog
        open={openModal}
        onOpenChange={setOpenModal}
        selectedAction={selectedAction}
        actionInspectData={actionInspectData}
        actionInspectLoading={actionInspectLoading}
        onClear={clearActionInspectData}
      />
    </div>
  );
};
